import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Button, Container, Typography, Alert, Grid, useTheme, alpha, Chip, Stack, CircularProgress } from '@mui/material';
import { VideoCall, Security, Person, CheckCircle, Verified, Shield, Home, CheckCircleOutline, Stars } from '@mui/icons-material';
// Dialog imports removed
import { createCustomVideoKyc, checkStatusVideoKyc } from 'utils/API/Merchant APIs/MerchantApiCalls';
import { motion, AnimatePresence } from 'framer-motion';
import MainCard from 'ui-component/cards/MainCard';
import { OpenInNew, Refresh } from '@mui/icons-material';

const VideoKYCPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [kycData, setKycData] = useState(null);
    const [error, setError] = useState(null);
    const [verificationStatus, setVerificationStatus] = useState(null);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState(null);
    const [feedbackSeverity, setFeedbackSeverity] = useState('info');
    const ensureHttps = (url) => {
        if (!url) return url;
        const modifiedUrl = url.trim().replace(/^http:\/\//i, 'https://');
        console.log('Video KYC URL Transformation:', {
            original: url,
            modified: modifiedUrl
        });
        return modifiedUrl;
    };

    const handleBackToHome = () => {
        // Update localStorage to reflect verified status before navigation
        // This prevents the route guard from redirecting back to KYC page
        localStorage.setItem('pixlKycStatus', 'Verified');
        localStorage.setItem('pixlKycVerificationStatus', 'pending');
        localStorage.setItem('kycPending', 'true');
        localStorage.setItem('kycRestricted', 'false');

        navigate('/merchant-home');
        window.location.reload();
    };
    const handleCreateKYC = async () => {
        setLoading(true);
        setError(null);
        setKycData(null);

        try {
            const agentId = localStorage.getItem('id');

            if (!agentId) {
                throw new Error('Agent ID not found. Please login again.');
            }

            const response = await createCustomVideoKyc({ agentid: agentId });

            if (response?.data?.success) {
                const updatedData = {
                    ...response.data,
                    vcip_url: ensureHttps(response.data.vcip_url)
                };
                setKycData(updatedData);
                // Open in new tab
                window.open(updatedData.vcip_url, '_blank');
            } else {
                throw new Error(response?.data?.message || 'Failed to create Video KYC session');
            }
        } catch (err) {
            console.error('Error creating Video KYC:', err);
            setError(err.message || 'An error occurred while creating Video KYC session');
        } finally {
            setLoading(false);
        }
    };

    const checkStatus = async () => {
        if (kycData?.unique_id) {
            setCheckingStatus(true);
            try {
                const agentId = localStorage.getItem('id');
                const response = await checkStatusVideoKyc({
                    agentid: agentId,
                    uniqueid: kycData.unique_id
                });
                const status = response?.data?.session?.status;

                if (status?.toLowerCase() === 'verified') {
                    setVerificationStatus('Verified');
                    setFeedbackMessage(null);
                } else if (status?.toLowerCase() === 'on call') {
                    setFeedbackMessage('Status is "On Call". Please complete the video call in the open tab.');
                    setFeedbackSeverity('info');
                } else {
                    setFeedbackMessage(`Current Status: ${status || 'Pending'}. Please complete the verification process.`);
                    setFeedbackSeverity('warning');
                }
            } catch (err) {
                console.error('Error checking KYC status:', err);
                setError(err.message || 'Error checking status');
                setFeedbackMessage(null);
            } finally {
                setCheckingStatus(false);
            }
        }
    };

    // Animated Background Patterns
    const BackgroundPattern = () => (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                opacity: 0.4
            }}
        >
            {/* Gradient Orbs */}
            <motion.div
                animate={{
                    scale: [1, 1.2, 1],
                    rotate: [0, 180, 360],
                    opacity: [0.3, 0.5, 0.3]
                }}
                transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute',
                    top: '-10%',
                    right: '-5%',
                    width: '500px',
                    height: '500px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(0,198,255,0.15) 0%, rgba(0,114,255,0.05) 70%)',
                    filter: 'blur(60px)'
                }}
            />
            <motion.div
                animate={{
                    scale: [1, 1.3, 1],
                    rotate: [360, 180, 0],
                    opacity: [0.2, 0.4, 0.2]
                }}
                transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute',
                    bottom: '-10%',
                    left: '-5%',
                    width: '600px',
                    height: '600px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(0,114,255,0.15) 0%, rgba(0,198,255,0.05) 70%)',
                    filter: 'blur(60px)'
                }}
            />

            {/* Grid Pattern */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `
                        linear-gradient(rgba(0,114,255,0.03) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(0,114,255,0.03) 1px, transparent 1px)
                    `,
                    backgroundSize: '50px 50px',
                    opacity: 0.5
                }}
            />
        </Box>
    );

    const InfoCard = ({ icon: Icon, label, value, delay }) => (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.5 }}>
            <MainCard
                sx={{
                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
                    borderRadius: '16px',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                        borderColor: alpha(theme.palette.primary.main, 0.3)
                    }
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: '12px',
                                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
                                    theme.palette.primary.main,
                                    0.05
                                )} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Icon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.secondary',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    fontWeight: 600,
                                    fontSize: '0.7rem'
                                }}
                            >
                                {label}
                            </Typography>
                        </Box>
                    </Stack>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: 'text.primary',
                            wordBreak: 'break-all',
                            fontFamily: label && label.includes('ID') ? 'monospace' : 'inherit',
                            fontSize: '1rem'
                        }}
                    >
                        {value}
                    </Typography>
                </Box>
            </MainCard>
        </motion.div>
    );

    InfoCard.propTypes = {
        icon: PropTypes.elementType.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        delay: PropTypes.number.isRequired
    };

    return (
        <>
            <BackgroundPattern />
            <Container
                maxWidth="md"
                sx={{ py: 2, position: 'relative', zIndex: 0, minHeight: '100vh', display: 'flex', alignItems: 'center' }}
            >
                <Box sx={{ width: '100%' }}>
                    <AnimatePresence mode="wait">
                        {!kycData ? (
                            <motion.div
                                key="create"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.5 }}
                            >
                                <MainCard
                                    sx={{
                                        background:
                                            theme.palette.mode === 'dark'
                                                ? 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)'
                                                : 'linear-gradient(180deg, #ffffff 0%, rgba(255,255,255,0.95) 100%)',
                                        borderRadius: '28px',
                                        overflow: 'hidden',
                                        position: 'relative',
                                        border: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                        boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.12)}`
                                    }}
                                >
                                    {/* Animated geometric decorations */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: -80,
                                            right: -80,
                                            width: 200,
                                            height: 200,
                                            borderRadius: '50%',
                                            background: `radial-gradient(circle, ${alpha(
                                                theme.palette.primary.main,
                                                0.12
                                            )} 0%, transparent 70%)`,
                                            filter: 'blur(40px)',
                                            animation: 'pulse 4s ease-in-out infinite'
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: -60,
                                            left: -60,
                                            width: 180,
                                            height: 180,
                                            borderRadius: '50%',
                                            background: `radial-gradient(circle, ${alpha(
                                                theme.palette.success.main,
                                                0.08
                                            )} 0%, transparent 70%)`,
                                            filter: 'blur(35px)',
                                            animation: 'pulse 6s ease-in-out infinite'
                                        }}
                                    />

                                    <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                                        {/* Hero Icon Section with Animated Rings */}
                                        <Box sx={{ position: 'relative', mb: 2.5, display: 'inline-block' }}>
                                            {/* Animated rings */}
                                            {[0, 1].map((ring) => (
                                                <motion.div
                                                    key={ring}
                                                    animate={{
                                                        scale: [1, 1.3, 1],
                                                        opacity: [0.4, 0, 0.4]
                                                    }}
                                                    transition={{
                                                        duration: 3,
                                                        repeat: Infinity,
                                                        delay: ring * 1,
                                                        ease: 'easeOut'
                                                    }}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        width: '110px',
                                                        height: '110px',
                                                        borderRadius: '50%',
                                                        border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                                        pointerEvents: 'none'
                                                    }}
                                                />
                                            ))}

                                            {/* Main Icon Container */}
                                            <motion.div
                                                animate={{
                                                    y: [0, -10, 0],
                                                    rotate: [0, 3, -3, 0]
                                                }}
                                                transition={{
                                                    duration: 5,
                                                    repeat: Infinity,
                                                    ease: 'easeInOut'
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 110,
                                                        height: 110,
                                                        borderRadius: '26px',
                                                        background: `linear-gradient(135deg, ${alpha(
                                                            theme.palette.primary.main,
                                                            0.2
                                                        )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        position: 'relative',
                                                        border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                                        boxShadow: `
                                                        0 20px 50px ${alpha(theme.palette.primary.main, 0.25)},
                                                        inset 0 0 30px ${alpha(theme.palette.primary.main, 0.05)}
                                                    `,
                                                        overflow: 'hidden',
                                                        '&::before': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            top: '-50%',
                                                            left: '-50%',
                                                            width: '200%',
                                                            height: '200%',
                                                            background: `conic-gradient(
                                                            from 0deg,
                                                            transparent 0deg 270deg,
                                                            ${alpha(theme.palette.primary.main, 0.3)} 270deg 360deg
                                                        )`,
                                                            animation: 'rotate 4s linear infinite'
                                                        }
                                                    }}
                                                >
                                                    {/* Inner glow */}
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            inset: '10px',
                                                            borderRadius: '20px',
                                                            background:
                                                                theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)',
                                                            backdropFilter: 'blur(10px)'
                                                        }}
                                                    />

                                                    {/* Shimmer effect */}
                                                    <motion.div
                                                        animate={{
                                                            x: [-150, 150],
                                                            opacity: [0, 0.8, 0]
                                                        }}
                                                        transition={{
                                                            duration: 2.5,
                                                            repeat: Infinity,
                                                            ease: 'easeInOut',
                                                            repeatDelay: 1
                                                        }}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '50%',
                                                            height: '100%',
                                                            background:
                                                                'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                                            filter: 'blur(10px)'
                                                        }}
                                                    />

                                                    {/* Icon with pulse */}
                                                    <motion.div
                                                        animate={{
                                                            scale: [1, 1.08, 1]
                                                        }}
                                                        transition={{
                                                            duration: 2,
                                                            repeat: Infinity,
                                                            ease: 'easeInOut'
                                                        }}
                                                        style={{ position: 'relative', zIndex: 1 }}
                                                    >
                                                        <VideoCall sx={{ fontSize: 50, color: theme.palette.primary.main }} />
                                                    </motion.div>
                                                </Box>
                                            </motion.div>
                                        </Box>

                                        {/* Add keyframes for rotation */}
                                        <style>
                                            {`
                                            @keyframes rotate {
                                                from { transform: rotate(0deg); }
                                                to { transform: rotate(360deg); }
                                            }
                                            @keyframes pulse {
                                                0%, 100% { opacity: 0.3; transform: scale(1); }
                                                50% { opacity: 0.5; transform: scale(1.1); }
                                            }
                                        `}
                                        </style>

                                        {/* Title with animated gradient */}
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 }}
                                        >
                                            <Typography
                                                variant="h4"
                                                gutterBottom
                                                sx={{
                                                    fontWeight: 800,
                                                    mb: 1,
                                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 60%, ${theme.palette.primary.main} 100%)`,
                                                    backgroundSize: '200% auto',
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                    animation: 'gradient 3s ease infinite',
                                                    letterSpacing: '-0.5px'
                                                }}
                                            >
                                                Video KYC Verification
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                    fontWeight: 600,
                                                    mb: 2,
                                                    letterSpacing: '1.5px',
                                                    textTransform: 'uppercase',
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                Instant • Secure • Mandatory
                                            </Typography>
                                        </motion.div>

                                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    mb: 3,
                                                    maxWidth: '500px',
                                                    mx: 'auto',
                                                    lineHeight: 1.6,
                                                    fontSize: '0.95rem'
                                                }}
                                            >
                                                To continue using services, Please complete a quick Video KYC verification.
                                            </Typography>
                                        </motion.div>

                                        <style>
                                            {`
                                            @keyframes gradient {
                                                0% { background-position: 0% 50%; }
                                                50% { background-position: 100% 50%; }
                                                100% { background-position: 0% 50%; }
                                            }
                                        `}
                                        </style>

                                        {/* Premium CTA Button */}
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.95 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: 0.4 }}
                                        >
                                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                                {/* Glow effect behind button */}
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: '-8px',
                                                        background: `radial-gradient(circle, ${alpha(
                                                            theme.palette.primary.main,
                                                            0.35
                                                        )} 0%, transparent 70%)`,
                                                        filter: 'blur(15px)',
                                                        opacity: loading ? 1 : 0.5,
                                                        transition: 'opacity 0.3s',
                                                        animation: loading ? 'pulse 2s ease-in-out infinite' : 'none'
                                                    }}
                                                />

                                                <Button
                                                    component={motion.button}
                                                    whileHover={{ scale: 1.03, y: -2 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    variant="contained"
                                                    size="large"
                                                    onClick={handleCreateKYC}
                                                    disabled={loading}
                                                    sx={{
                                                        position: 'relative',
                                                        py: 1.8,
                                                        px: 6,
                                                        borderRadius: '50px',
                                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.primary.main} 100%)`,
                                                        backgroundSize: '200% 100%',
                                                        fontSize: '1rem',
                                                        fontWeight: 700,
                                                        textTransform: 'none',
                                                        boxShadow: `
                                                        0 12px 28px ${alpha(theme.palette.primary.main, 0.3)},
                                                        0 4px 12px ${alpha(theme.palette.primary.main, 0.2)},
                                                        inset 0 -2px 8px ${alpha(theme.palette.primary.dark, 0.4)}
                                                    `,
                                                        border: `1.5px solid ${alpha('#fff', 0.15)}`,
                                                        overflow: 'hidden',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            backgroundPosition: '100% 0',
                                                            boxShadow: `
                                                            0 16px 36px ${alpha(theme.palette.primary.main, 0.4)},
                                                            0 6px 16px ${alpha(theme.palette.primary.main, 0.25)},
                                                            inset 0 -2px 8px ${alpha(theme.palette.primary.dark, 0.4)}
                                                        `
                                                        },
                                                        '&::before': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            inset: 0,
                                                            background:
                                                                'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)',
                                                            backgroundSize: '200% 200%',
                                                            animation: 'shimmer 3s linear infinite'
                                                        }
                                                    }}
                                                    startIcon={loading ? null : <VideoCall sx={{ fontSize: 24 }} />}
                                                >
                                                    {loading ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <motion.div
                                                                animate={{ rotate: 360 }}
                                                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                                                style={{
                                                                    width: 20,
                                                                    height: 20,
                                                                    border: '2.5px solid rgba(255,255,255,0.3)',
                                                                    borderTopColor: '#fff',
                                                                    borderRadius: '50%'
                                                                }}
                                                            />
                                                            <span>Generating Link...</span>
                                                        </Box>
                                                    ) : (
                                                        'Generate Verification Link'
                                                    )}
                                                </Button>
                                            </Box>

                                            <style>
                                                {`
                                                @keyframes shimmer {
                                                    0% { background-position: -200% -200%; }
                                                    100% { background-position: 200% 200%; }
                                                }
                                            `}
                                            </style>
                                        </motion.div>
                                    </Box>
                                </MainCard>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="result"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                {verificationStatus === 'Verified' ? (
                                    <MainCard
                                        sx={{
                                            background:
                                                theme.palette.mode === 'dark'
                                                    ? 'linear-gradient(135deg, rgba(16,185,129,0.05) 0%, rgba(5,150,105,0.02) 100%)'
                                                    : 'linear-gradient(135deg, rgba(16,185,129,0.03) 0%, rgba(255,255,255,1) 100%)',
                                            borderRadius: '32px',
                                            overflow: 'visible',
                                            position: 'relative',
                                            border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                            boxShadow: `0 24px 80px ${alpha(theme.palette.success.main, 0.15)}, 0 0 0 1px ${alpha(
                                                theme.palette.success.main,
                                                0.1
                                            )}`,
                                            textAlign: 'center',
                                            p: { xs: 5, md: 8 },
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: '200px',
                                                background: `linear-gradient(180deg, ${alpha(
                                                    theme.palette.success.main,
                                                    0.08
                                                )} 0%, transparent 100%)`,
                                                borderRadius: '32px 32px 0 0',
                                                pointerEvents: 'none'
                                            }
                                        }}
                                    >
                                        {/* Decorative Elements */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: -20,
                                                right: -20,
                                                width: 100,
                                                height: 100,
                                                borderRadius: '50%',
                                                background: `radial-gradient(circle, ${alpha(
                                                    theme.palette.success.light,
                                                    0.3
                                                )} 0%, transparent 70%)`,
                                                filter: 'blur(20px)',
                                                pointerEvents: 'none'
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: -30,
                                                left: -30,
                                                width: 120,
                                                height: 120,
                                                borderRadius: '50%',
                                                background: `radial-gradient(circle, ${alpha(
                                                    theme.palette.primary.light,
                                                    0.2
                                                )} 0%, transparent 70%)`,
                                                filter: 'blur(25px)',
                                                pointerEvents: 'none'
                                            }}
                                        />

                                        {/* Success Icon with Enhanced Animation */}
                                        <motion.div
                                            initial={{ scale: 0, rotate: -180 }}
                                            animate={{ scale: 1, rotate: 0 }}
                                            transition={{ type: 'spring', stiffness: 150, damping: 15, delay: 0.1 }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 140,
                                                    height: 140,
                                                    borderRadius: '50%',
                                                    background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    margin: '0 auto',
                                                    mb: 4,
                                                    position: 'relative',
                                                    boxShadow: `0 12px 40px ${alpha(theme.palette.success.main, 0.4)}, 0 0 0 8px ${alpha(
                                                        theme.palette.success.main,
                                                        0.1
                                                    )}, 0 0 0 16px ${alpha(theme.palette.success.main, 0.05)}`
                                                }}
                                            >
                                                <motion.div
                                                    animate={{
                                                        scale: [1, 1.2, 1],
                                                        opacity: [0.5, 0, 0.5]
                                                    }}
                                                    transition={{
                                                        duration: 2,
                                                        repeat: Infinity,
                                                        ease: 'easeInOut'
                                                    }}
                                                    style={{
                                                        position: 'absolute',
                                                        inset: -20,
                                                        borderRadius: '50%',
                                                        border: `3px solid ${theme.palette.success.main}`,
                                                        pointerEvents: 'none'
                                                    }}
                                                />
                                                <CheckCircleOutline sx={{ fontSize: 70, color: '#fff' }} />
                                            </Box>
                                        </motion.div>

                                        {/* Success Message */}
                                        <motion.div
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.3, duration: 0.6 }}
                                        >
                                            <Stack spacing={1} alignItems="center" sx={{ mb: 3 }}>
                                                <motion.div
                                                    initial={{ opacity: 0, scale: 0.8 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    transition={{ delay: 0.4 }}
                                                >
                                                    <Chip
                                                        icon={<Stars />}
                                                        label="Verification Complete"
                                                        size="small"
                                                        sx={{
                                                            background: `linear-gradient(135deg, ${alpha(
                                                                theme.palette.success.main,
                                                                0.15
                                                            )} 0%, ${alpha(theme.palette.success.light, 0.1)} 100%)`,
                                                            color: theme.palette.success.dark,
                                                            fontWeight: 700,
                                                            fontSize: '0.75rem',
                                                            border: `1.5px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                                            px: 1
                                                        }}
                                                    />
                                                </motion.div>
                                                <Typography
                                                    variant="h2"
                                                    sx={{
                                                        fontWeight: 900,
                                                        background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                                                        backgroundClip: 'text',
                                                        WebkitBackgroundClip: 'text',
                                                        WebkitTextFillColor: 'transparent',
                                                        letterSpacing: '-0.02em',
                                                        mb: 1
                                                    }}
                                                >
                                                    Video-KYC Verified!
                                                </Typography>
                                            </Stack>
                                            <Typography
                                                variant="h6"
                                                color="text.secondary"
                                                sx={{
                                                    mb: 5,
                                                    maxWidth: '550px',
                                                    mx: 'auto',
                                                    fontWeight: 500,
                                                    lineHeight: 1.6
                                                }}
                                            >
                                                The video KYC process has been completed successfully. Customer identity verification is now
                                                complete.
                                            </Typography>
                                        </motion.div>

                                        {/* Customer ID Card */}
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.5 }}
                                        >
                                            <Box
                                                sx={{
                                                    p: 4,
                                                    borderRadius: '24px',
                                                    background:
                                                        theme.palette.mode === 'dark'
                                                            ? `linear-gradient(135deg, ${alpha(
                                                                  theme.palette.primary.main,
                                                                  0.08
                                                              )} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`
                                                            : `linear-gradient(135deg, ${alpha(
                                                                  theme.palette.primary.main,
                                                                  0.06
                                                              )} 0%, ${alpha(theme.palette.primary.light, 0.03)} 100%)`,
                                                    border: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                                    display: 'inline-block',
                                                    mb: 6,
                                                    minWidth: '300px',
                                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`
                                                }}
                                            >
                                                <Stack spacing={2} alignItems="center">
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            borderRadius: '12px',
                                                            background: alpha(theme.palette.primary.main, 0.1),
                                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                                                        }}
                                                    >
                                                        <Person sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                                                    </Box>
                                                    <Box>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '1.5px',
                                                                fontWeight: 700,
                                                                fontSize: '0.7rem'
                                                            }}
                                                        >
                                                            Customer ID
                                                        </Typography>
                                                        <Typography
                                                            variant="h3"
                                                            sx={{
                                                                mt: 1,
                                                                fontFamily: 'monospace',
                                                                fontWeight: 800,
                                                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                                backgroundClip: 'text',
                                                                WebkitBackgroundClip: 'text',
                                                                WebkitTextFillColor: 'transparent',
                                                                letterSpacing: '0.05em'
                                                            }}
                                                        >
                                                            {kycData.customer_id}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        </motion.div>

                                        {/* Action Button */}
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.6 }}
                                        >
                                            <Button
                                                variant="contained"
                                                size="large"
                                                onClick={handleBackToHome}
                                                startIcon={<Home />}
                                                sx={{
                                                    py: 2,
                                                    px: 5,
                                                    borderRadius: '60px',
                                                    fontSize: '1.1rem',
                                                    fontWeight: 700,
                                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.35)}, 0 0 0 4px ${alpha(
                                                        theme.palette.primary.main,
                                                        0.1
                                                    )}`,
                                                    textTransform: 'none',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: `0 16px 40px ${alpha(
                                                            theme.palette.primary.main,
                                                            0.4
                                                        )}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}`
                                                    }
                                                }}
                                            >
                                                Go to Home
                                            </Button>
                                        </motion.div>

                                        {/* Trust Badge */}
                                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
                                            <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center" sx={{ mt: 5 }}>
                                                <Shield sx={{ fontSize: 16, color: theme.palette.success.main }} />
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                                                >
                                                    Verified & Secured • ISO 27001 Certified
                                                </Typography>
                                            </Stack>
                                        </motion.div>
                                    </MainCard>
                                ) : (
                                    <Stack spacing={3}>
                                        {/* Success Banner */}
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.95 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: 0.1 }}
                                        >
                                            <MainCard
                                                sx={{
                                                    background: `linear-gradient(135deg, ${alpha(
                                                        theme.palette.success.main,
                                                        0.1
                                                    )} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                                                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                                    borderRadius: '16px'
                                                }}
                                            >
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            borderRadius: '12px',
                                                            background: alpha(theme.palette.success.main, 0.15),
                                                            color: theme.palette.success.main
                                                        }}
                                                    >
                                                        <CheckCircle sx={{ fontSize: 32 }} />
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant="h5"
                                                            sx={{ fontWeight: 700, color: theme.palette.success.main }}
                                                        >
                                                            Verification Link Created Successfully
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                            A new tab has opened for your Video KYC.
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        icon={<Security />}
                                                        label="In Progress"
                                                        size="small"
                                                        sx={{
                                                            background: alpha(theme.palette.success.main, 0.2),
                                                            color: theme.palette.success.main,
                                                            fontWeight: 600,
                                                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                                                        }}
                                                    />
                                                </Stack>
                                            </MainCard>
                                        </motion.div>

                                        {/* Action Buttons */}
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 }}
                                        >
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        size="large"
                                                        onClick={() => window.open(kycData.vcip_url, '_blank')}
                                                        startIcon={<OpenInNew />}
                                                        sx={{
                                                            py: 1.5,
                                                            borderRadius: '12px',
                                                            background: alpha(theme.palette.primary.main, 0.1),
                                                            color: theme.palette.primary.main,
                                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                                            boxShadow: 'none',
                                                            '&:hover': {
                                                                background: alpha(theme.palette.primary.main, 0.2),
                                                                boxShadow: 'none'
                                                            }
                                                        }}
                                                    >
                                                        Open Video KYC Page
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        size="large"
                                                        onClick={checkStatus}
                                                        disabled={checkingStatus}
                                                        startIcon={
                                                            checkingStatus ? <CircularProgress size={20} color="inherit" /> : <Refresh />
                                                        }
                                                        sx={{
                                                            py: 1.5,
                                                            borderRadius: '12px',
                                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                            boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.25)}`
                                                        }}
                                                    >
                                                        {checkingStatus ? 'Checking...' : 'Check Verification Status'}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </motion.div>

                                        {/* Verification Status Loading Indicator */}
                                        {checkingStatus && (
                                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                                <Alert severity="info" variant="outlined" sx={{ borderRadius: '12px' }}>
                                                    Checking status with the server... please wait.
                                                </Alert>
                                            </motion.div>
                                        )}

                                        {/* Customer Information Cards */}
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={12}>
                                                <InfoCard icon={Person} label="Customer ID" value={kycData.customer_id} delay={0.2} />
                                            </Grid>
                                        </Grid>

                                        {/* Footer Actions */}
                                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Button
                                                    variant="text"
                                                    onClick={() => setKycData(null)}
                                                    startIcon={<VideoCall />}
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontWeight: 600,
                                                        '&:hover': {
                                                            color: theme.palette.primary.main,
                                                            background: alpha(theme.palette.primary.main, 0.05)
                                                        }
                                                    }}
                                                >
                                                    Start New Session
                                                </Button>

                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Verified sx={{ fontSize: 18, color: theme.palette.success.main }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Link is active
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </motion.div>
                                    </Stack>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Feedback Display */}
                    {feedbackMessage && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                            <Alert
                                severity={feedbackSeverity}
                                variant="filled"
                                sx={{
                                    borderRadius: '16px',
                                    mt: 2,
                                    py: 1,
                                    px: 3,
                                    boxShadow: theme.shadows[3]
                                }}
                            >
                                {feedbackMessage}
                            </Alert>
                        </motion.div>
                    )}

                    {/* Error Display */}
                    {error && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                            <Alert
                                severity="error"
                                variant="filled"
                                icon={<Security />}
                                sx={{
                                    borderRadius: '16px',
                                    mt: 3,
                                    py: 2,
                                    px: 3,
                                    background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
                                    boxShadow: `0 8px 24px ${alpha(theme.palette.error.main, 0.3)}`,
                                    '& .MuiAlert-message': {
                                        fontSize: '1rem',
                                        fontWeight: 500
                                    }
                                }}
                            >
                                {error}
                            </Alert>
                        </motion.div>
                    )}
                </Box>
            </Container>

            {/* Iframe Dialog Removed */}
        </>
    );
};

export default VideoKYCPage;
